// src/services/ProjectService.ts
import axios from 'axios';
import { environment } from 'src/environments/environment';
import AuthService from 'src/app/polar-SAAS-Components/auth-service';
import axiosInstance  from 'src/app/polar-SAAS-Components/axios-instance';
const API_BASE_URL = environment.host;

interface SmsCredit {
  id: string;
  corporateId: string;
  balance: number;
  lastTopUp: Date;
  lastUsed: Date;
}

interface SmsPurchaseParams {
  count: number;
  isPaid: boolean;
  remarks?: string;
  paymentDate?: string;
  referenceNumber?: string;
  pricePerSms?: number;
}

class ProjectService {
  // Get current SMS credit balance
  static async getSmsCreditBalance(corporateId: string): Promise<number> {
    const response = await axios.get<SmsCredit>(
      `${API_BASE_URL}/corporate/${corporateId}/sms-balance`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('ACCESS_TOKEN')}`,
        },
      }
    );
    return response.data.balance;
  }

  // Purchase SMS credits (enhanced version)
  static async purchaseSmsCredits(
  corporateId: string,
  params: SmsPurchaseParams
): Promise<SmsCredit> {
  // Get the current user ID from localStorage
  const userId = localStorage.getItem('USER_ID');
  
  // Construct the correct URL with user ID and corporate ID
  const url = `${API_BASE_URL}/user/${userId}/corporate/${corporateId}/purchase-sms`;
  
  try {
    const response = await axios.post<SmsCredit>(
      url,
      {}, // empty body since we're using query params
      {
        params: {
          count: params.count,
          isPaid: params.isPaid,
          remarks: params.remarks || '',
          pricePerSms: params.pricePerSms || 0.5, // default value if not provided
          ...(params.isPaid && {
            paymentDate: params.paymentDate,
            referenceNumber: params.referenceNumber
          })
        },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('ACCESS_TOKEN')}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error purchasing SMS credits:", error);
    throw error;
  }
}

  // Get SMS transaction history
  static async getSmsTransactionHistory(
    corporateId: string,
    page: number = 1,
    pageSize: number = 10
  ): Promise<any> {
    const response = await axios.get(
      `${API_BASE_URL}/corporate/${corporateId}/sms-transactions`,
      {
        params: { page, size: pageSize },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('ACCESS_TOKEN')}`,
        },
      }
    );
    return response.data;
  }

  // Get corporate details including SMS info
static async getKitSmsDetailsByCorporate(corporateId: string): Promise<any> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/user/${localStorage.getItem('USER_ID')}/corporate/${corporateId}/details`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('ACCESS_TOKEN')}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
}

static async getKitAlertDetailsByCorporateandMode(corporateId: string,mode:string): Promise<any> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/user/${localStorage.getItem('USER_ID')}/corporate/${corporateId}/alert-count?mode=${mode}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('ACCESS_TOKEN')}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
}

static async getKitAlertDetailsByCorporateandModeandDate(corporateId: string,mode:string,from:string,to:string): Promise<any> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/user/${localStorage.getItem('USER_ID')}/corporate/${corporateId}/alert-count-project?mode=${mode}&from=${from}&to=${to}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('ACCESS_TOKEN')}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    throw error;
  }
}
  // Get all corporates for the current user
  static async getCorporates(): Promise<any> {
    const response = await axios.get(
      `${API_BASE_URL}/user/${localStorage.getItem('USER_ID')}/corporate`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('ACCESS_TOKEN')}`,
        },
      }
    );
    return response.data;
  }

  // Get projects for user (optionally filtered by corporate)
  static async getProjectsForUser(corporateId: string | null = null): Promise<any> {
    let url = `${API_BASE_URL}/user/${localStorage.getItem('USER_ID')}/project`;
    if (corporateId) url += `?corporate=${corporateId}`;

    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('ACCESS_TOKEN')}`,
      },
    });
    return response;
  }
  
  async getProject(projectId){
    return await axiosInstance.get('/user/' + AuthService.getUserId() + '/project/' + projectId)
  };

  async updateProject(projectId,details){
    return await axiosInstance.put('/user/' + AuthService.getUserId() + '/project/' + projectId,details)
  };
  
  async getKit(kitId: string) {
    return await axiosInstance.get(`/user/${AuthService.getUserId()}/kit/${kitId}`);
  }

  async getAlertLimitForProperty(kitId, propertyId){
    return await axiosInstance.get(`/user/${AuthService.getUserId()}/kit/${kitId}/property/${propertyId}/alert-limit`);
  }

  async getAlertLimitForPropertyHistory(kitId, propertyId, offset, entityCount){
    return await axiosInstance.get('/user/' + AuthService.getUserId() + '/kit/' + kitId + '/property/' + propertyId +
        '/alert-limit/history/size/' + entityCount + '/index/' + offset)
  }

  async  getAlertToggleHistory(deviceId, offset: number, entityCount: number) {
      return  await axiosInstance.get( '/user/' + AuthService.getUserId() + '/kit/' + deviceId + '/alert/size/' + entityCount + '/index/' + offset,

      );
    }
  async getUserActionHistory(userId, offset, page){
    return await axiosInstance.get('/ums/user/' + AuthService.getUserId() + '/system-user/' + userId + '/history?size=' + offset +
      '&page=' + page + '&order=DESC')
  }
  async updateKitAlertLimit(kitId, propertyId, entity: any) {
      return await axiosInstance.put( '/user/' + AuthService.getUserId() + '/kit/' + kitId + '/property/' + propertyId + '/alert-limit',
        entity)
      
    }
  
  async getMaintenanceHistory(kitId: string, offset: number, entityCount: number) {
    return await axiosInstance.get(`/user/${AuthService.getUserId()}/kit/${kitId}/maintain/size/${entityCount}/index/${offset}`);
  }
  async updateKitMaintenanceStatus(kitId: string, entity: any) {
    return await axiosInstance.put('/user/' + AuthService.getUserId() + '/kit/' + kitId + '/maintain',
          entity);
  }
  async updateKitPersistenceDeviceStatus(kitId: string, entity: any) {
    return await axiosInstance.put('/user/' + AuthService.getUserId() + '/kit/' + kitId + '/persistence',
          entity);
  }
  async updateKitSuppressAlert(kitId: string, entity: any) {
    return await axiosInstance.put('/user/' + AuthService.getUserId() + '/kit/' + kitId + '/defrost',
      entity,);
  }
  async updateAlertStatus(kitId,alert:any){
    return await axiosInstance.put('/user/' + AuthService.getUserId() + '/kit/' + kitId + '/alert',
          alert)
  }
  async addKitToProject(projectId,entity){
    return await axiosInstance.post('/user/' + AuthService.getUserId() + '/project/' + projectId + '/kit',
          entity)
  }
  async updateKit(kitId,entity  ){
    return await axiosInstance.put('/user/' + AuthService.getUserId() + '/kit/' + kitId,
          entity)}

  async deleteKit (projectId: string,kitId:string) {
    const path = `/user/${AuthService.getUserId()}/project/${projectId}/kit/${kitId}`;
    const response = await axiosInstance.delete(path);
            return response;
          };
}

export default ProjectService;